import { Alert, AlertDescription, AlertTitle, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Progress, useChat } from "randmarcomps";
import { AlertCircle, Clock4, Plus, Search, ShieldCheck, Sparkles, Users } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { OpportunityEntry, useGetV4ManufacturerByRouteManufacturerIdOpportunitiesQuery } from "randmar-api-client";
import OpportunitiesGrid from "../components/grids/OpportunitiesGrid";
import CreateNewOpportunitiesSteps from "../components/opportunities/CreateNewOpportunitiesSteps";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import InlineSpinner from "../components/util/InlineSpinner";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import withRoot from "../withRoot";

type FilterKey = "active" | "defaultStatus" | "defaultResellers" | "expiringSoon";

const defaultFiltersState: Record<FilterKey, boolean> = {
  active: true,
  defaultStatus: false,
  defaultResellers: false,
  expiringSoon: false,
};

const filterLabelMap: Record<FilterKey, string> = {
  active: "Active",
  defaultStatus: "Default status",
  defaultResellers: "Default resellers",
  expiringSoon: "Expiring soon",
};

const parseDateOrNull = (dateValue?: string | null) => {
  if (!dateValue) return null;
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate;
};

const hasEndDateInFuture = (endDate?: string | null) => {
  const parsedEndDate = parseDateOrNull(endDate);
  if (!parsedEndDate) return false;

  const now = new Date();
  return parsedEndDate >= now;
};

const isOpportunityActive = (opportunity: OpportunityEntry) =>
  Boolean(opportunity.Active) && hasEndDateInFuture(opportunity.EndDate);

const isOpportunityExpiringSoon = (endDate?: string | null) => {
  const parsedEndDate = parseDateOrNull(endDate);
  if (!parsedEndDate) return false;

  const now = new Date();
  const threshold = new Date(now.getTime());
  threshold.setDate(threshold.getDate() + 30);

  return parsedEndDate >= now && parsedEndDate <= threshold;
};

const matchesSearchTerm = (opportunity: OpportunityEntry, normalizedSearch: string) => {
  if (!normalizedSearch) return true;

  return [
    opportunity.BidNumber,
    opportunity.Opportunity,
    opportunity.Description,
    opportunity.DefaultResellers,
  ].some(value => (value || "").toLowerCase().includes(normalizedSearch));
};

function buildOpportunitiesAssistantPrompt(options: {
  applicationId: string;
  totalOpportunities: number;
  visibleOpportunities: number;
  searchTerm: string;
  filters: Record<FilterKey, boolean>;
}) {
  const search = options.searchTerm.trim();
  const enabledFilters = (Object.keys(options.filters) as FilterKey[])
    .filter((key) => options.filters[key])
    .map((key) => filterLabelMap[key]);

  const filterLabel = enabledFilters.length > 0 ? enabledFilters.join(", ") : "None";

  return [
    "You are assisting with the Randmar Manufacturer Portal Opportunities page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Total opportunities loaded: ${options.totalOpportunities}`,
    `Visible opportunities (after filtering): ${options.visibleOpportunities}`,
    `Current filters: ${filterLabel}${search ? `, search="${search}"` : ""}`,
    "",
    "Help me interpret what I'm seeing and recommend next actions.",
  ].join("\n");
}

const Opportunities: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { sendMessageWithClear } = useChat();
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);

  const {
    data: opportunitiesData,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    refetch,
  } = useGetV4ManufacturerByRouteManufacturerIdOpportunitiesQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const loading = isLoading;

  const opportunities: OpportunityEntry[] = React.useMemo(
    () => (Array.isArray(opportunitiesData) ? (opportunitiesData as OpportunityEntry[]) : []),
    [opportunitiesData]
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filters, setFilters] = React.useState<Record<FilterKey, boolean>>(() => ({ ...defaultFiltersState }));
  const [showFilterSpinner, setShowFilterSpinner] = React.useState(false);

  React.useEffect(() => {
    if (!isFetching && (isSuccess || isError)) {
      setLastRefreshedAt(new Date());
    }
  }, [isError, isFetching, isSuccess]);

  const previousTitleRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    previousTitleRef.current = document.title;
    return () => {
      if (previousTitleRef.current) {
        document.title = previousTitleRef.current;
      }
    };
  }, []);

  const stats = React.useMemo(() => {
    const activeOpportunities = opportunities.filter(isOpportunityActive);
    const activeDefaultResellers = activeOpportunities.filter(opportunity => Boolean(opportunity.DefaultResellers));
    const activeChosenResellers = Math.max(activeOpportunities.length - activeDefaultResellers.length, 0);

    return [
      { label: "Total active opportunities", value: activeOpportunities.length.toString() },
      { label: "Active default resellers opportunities", value: activeDefaultResellers.length.toString() },
      { label: "Active chosen reseller opportunities", value: activeChosenResellers.toString() },
    ];
  }, [opportunities]);

  const filterOptions: { id: FilterKey; label: string; icon: React.ReactNode }[] = [
    { id: "active", label: filterLabelMap.active, icon: <Sparkles className="h-4 w-4" /> },
    { id: "defaultStatus", label: filterLabelMap.defaultStatus, icon: <ShieldCheck className="h-4 w-4" /> },
    { id: "defaultResellers", label: filterLabelMap.defaultResellers, icon: <Users className="h-4 w-4" /> },
    { id: "expiringSoon", label: filterLabelMap.expiringSoon, icon: <Clock4 className="h-4 w-4" /> },
  ];

  const normalizedSearch = React.useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);
  const isUsingDefaultFilters = React.useMemo(
    () =>
      filters.active === defaultFiltersState.active &&
      filters.defaultStatus === defaultFiltersState.defaultStatus &&
      filters.defaultResellers === defaultFiltersState.defaultResellers &&
      filters.expiringSoon === defaultFiltersState.expiringSoon,
    [filters]
  );

  const opportunitiesMatchingFilters = React.useMemo(() => {
    return opportunities.filter(opportunity => {
      return (
        (!filters.active || isOpportunityActive(opportunity)) &&
        (!filters.defaultStatus || Boolean(opportunity.CanBeSetAsDefault)) &&
        (!filters.defaultResellers || Boolean(opportunity.DefaultResellers)) &&
        (!filters.expiringSoon || isOpportunityExpiringSoon(opportunity.EndDate))
      );
    });
  }, [opportunities, filters]);

  const filteredOpportunities = React.useMemo(() => {
    if (!normalizedSearch) return opportunitiesMatchingFilters;
    return opportunitiesMatchingFilters.filter(opportunity => matchesSearchTerm(opportunity, normalizedSearch));
  }, [opportunitiesMatchingFilters, normalizedSearch]);

  const selectedFilterLabels = React.useMemo(() => {
    return (Object.keys(filters) as FilterKey[])
      .filter(filterKey => filters[filterKey])
      .map(filterKey => filterLabelMap[filterKey].toLowerCase());
  }, [filters]);

  const filterSummaryLabel = React.useMemo(() => {
    if (selectedFilterLabels.length === 0) return "opportunities";
    return `${selectedFilterLabels.join(" & ")} opportunities`;
  }, [selectedFilterLabels]);

  const showResultsSummary = React.useMemo(
    () => opportunities.length > 0 && (Boolean(normalizedSearch) || !isUsingDefaultFilters),
    [opportunities.length, normalizedSearch, isUsingDefaultFilters]
  );

  React.useEffect(() => {
    if (loading) {
      setShowFilterSpinner(false);
      return;
    }
    if (!normalizedSearch && isUsingDefaultFilters) {
      setShowFilterSpinner(false);
      return;
    }

    setShowFilterSpinner(true);
    const timer = setTimeout(() => setShowFilterSpinner(false), 400);
    return () => clearTimeout(timer);
  }, [normalizedSearch, isUsingDefaultFilters, loading]);

  React.useEffect(() => {
    const previousTitle = previousTitleRef.current;
    if (!previousTitle) return;

    const titleParts: string[] = [];
    if (!isUsingDefaultFilters) titleParts.push(filterSummaryLabel);
    if (normalizedSearch) titleParts.push(`Search: ${searchTerm.trim()}`);

    const prefix = titleParts.length === 0 ? "Opportunities" : `Opportunities — ${titleParts.join(" · ")}`;
    document.title = `${prefix} | ${previousTitle}`;
  }, [filterSummaryLabel, isUsingDefaultFilters, normalizedSearch, searchTerm]);

  const handleToggleFilter = React.useCallback((filterId: FilterKey) => {
    setFilters(prev => ({ ...prev, [filterId]: !prev[filterId] }));
  }, []);

  const handleResetFilters = React.useCallback(() => {
    setSearchTerm("");
    setFilters({ ...defaultFiltersState });
  }, []);

  const assistantPrompt = React.useMemo(() => {
    if (!applicationId) return "";
    return buildOpportunitiesAssistantPrompt({
      applicationId,
      totalOpportunities: opportunities.length,
      visibleOpportunities: filteredOpportunities.length,
      searchTerm,
      filters,
    });
  }, [applicationId, filteredOpportunities.length, filters, opportunities.length, searchTerm]);

  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";

  return (
    <div className={pageContainerClassName} aria-busy={isFetching}>
      <header className={surfaceClassName} role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Manufacturer Portal
              </span>
              {isFetching && (
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <InlineSpinner size="sm" decorative />
                  Syncing
                </span>
              )}
            </div>
            <h1 className="text-[32px] font-semibold tracking-tight text-slate-900">Opportunities</h1>
            <p className="max-w-2xl text-[15px] text-slate-600">
              Invite qualified resellers to targeted rebates, track active bids, and keep defaults aligned for the next offer.
            </p>
            {isFetching && <Progress className="mt-3 h-1" aria-label="Refreshing opportunities data" />}
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={isFetching}
              onRefresh={() => refetch()}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => assistantPrompt && sendMessageWithClear(assistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                  disabled={!assistantPrompt}
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />

            <Button asChild className="rounded-xl">
              <Link to={Path.createOpportunity(applicationId)}>
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                New opportunity
              </Link>
            </Button>
          </div>
        </div>

        <dl className="mt-6 grid gap-4 md:grid-cols-3" aria-label="Opportunity metrics">
          {stats.map(stat => (
            <div key={stat.label} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stat.label}</dt>
              <dd className="mt-1 text-2xl font-semibold text-slate-900">{stat.value}</dd>
            </div>
          ))}
        </dl>

        {isError && (
          <Alert variant="destructive" className="mt-6 rounded-2xl border border-red-100 bg-white">
            <AlertTitle>Couldn’t load opportunities</AlertTitle>
            <AlertDescription>Try refreshing this page.</AlertDescription>
          </Alert>
        )}
      </header>

      <section className={[surfaceClassName, "space-y-4"].join(" ")} aria-label="Search and filters">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name, bid number, or default reseller tier"
              className="h-11 w-full rounded-lg border-slate-200 bg-white pl-9 text-[15px]"
              aria-label="Search opportunities"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[color:var(--ops-accent)]"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {filterOptions.map(option => {
              const isActive = filters[option.id];
              const className = [
                "rounded-xl",
                isActive ? "border-transparent bg-[var(--ops-accent)] text-white hover:bg-[var(--ops-accent)]/90" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <Button
                  type="button"
                  key={option.id}
                  aria-pressed={isActive}
                  onClick={() => handleToggleFilter(option.id)}
                  variant="outline"
                  size="sm"
                  className={className}
                >
                  {option.icon}
                  {option.label}
                </Button>
              );
            })}
          </div>
        </div>

        {showResultsSummary && (
          <p className="text-[13px] text-slate-600" aria-live="polite">
            Showing <span className="font-semibold text-slate-900">{filteredOpportunities.length.toLocaleString()}</span> of{" "}
            <span className="font-semibold text-slate-900">{opportunitiesMatchingFilters.length.toLocaleString()}</span> {filterSummaryLabel}.
          </p>
        )}

        {showFilterSpinner && (
          <div className="flex items-center gap-2 text-[13px] text-slate-500" aria-live="polite">
            <InlineSpinner size="sm" tone="accent" decorative />
            Updating results…
          </div>
        )}
      </section>

      {loading && opportunities.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
          <CardHeader className="items-center space-y-3 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ops-accent)]">
              <InlineSpinner size="sm" decorative />
              Loading
            </div>
            <CardTitle className="text-2xl font-semibold text-slate-900">Fetching opportunities</CardTitle>
            <CardDescription className="text-[15px] text-slate-600">This usually takes a few seconds.</CardDescription>
          </CardHeader>
          <CardContent className="pb-6">
            <Progress className="h-1" aria-label="Loading opportunities" />
          </CardContent>
        </Card>
      ) : opportunities.length === 0 ? (
        <CreateNewOpportunitiesSteps />
      ) : filteredOpportunities.length === 0 ? (
        <NoOpportunitiesResults onReset={handleResetFilters} />
      ) : (
        <OpportunitiesGrid opportunities={filteredOpportunities} />
      )}
    </div>
  );
}

const NoOpportunitiesResults: React.FC<{ onReset: () => void }> = ({ onReset }) => (
  <Card className="rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
    <CardHeader>
      <CardTitle className="text-xl font-medium text-slate-900">No opportunities match what you’re looking for</CardTitle>
      <CardDescription className="text-[15px] text-slate-600">
        Update your filters or clear search to see every opportunity again.
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col gap-3 text-[15px] text-slate-600">
      <div className="inline-flex items-center gap-2 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2">
        <AlertCircle className="h-4 w-4 text-rose-600" aria-hidden="true" />
        Filters can be combined. Make sure you’re not limiting results unintentionally.
      </div>
      <Button onClick={onReset} variant="outline" className="self-start rounded-xl">
        Reset filters
      </Button>
    </CardContent>
  </Card>
);

export default withRoot(Opportunities);
